/**
 * The needs-repair banner (ACT2-1286 PR 2b, spec UI 11–13). It renders nothing while the item resolves
 * and nothing at all with the repair flag off (`view.repairEnabled` false), so the page is unchanged.
 */
import type { RepairItemRef } from '@server/warehouse-repair';
import type { AccountRepairView } from '@server/warehouse-repair-state';
import type { QueryKey } from '@tanstack/react-query';

import { Callout } from '@/_shared/components/ui/callout';

import { ASK_TO_REPAIR, removedHeadline, type RepairItemNoun, unassignedHeadline, unverifiedHeadline } from './copy';
import { RepairButton } from './RepairButton';

export function AccountRepairBanner({
  itemRef,
  itemNoun,
  view,
  invalidateKeys,
  detail,
}: {
  itemRef: RepairItemRef;
  itemNoun: RepairItemNoun;
  view: AccountRepairView;
  invalidateKeys: readonly QueryKey[];
  /** A second line for the needs-repair state (an audience says what still syncs). */
  detail?: string;
}): React.ReactElement | null {
  if (!view.repairEnabled || view.state === 'ok') return null;
  const unverified = view.state === 'unverified';
  const headline =
    view.state === 'needs_repair'
      ? removedHeadline(itemNoun)
      : view.state === 'unassigned'
        ? unassignedHeadline(itemNoun)
        : unverifiedHeadline(itemNoun, view.notice);
  const second = unverified
    ? view.notice
      ? `Repaired by ${view.notice.actorName ?? 'a former member'}. Repair again to pick an account that runs them all.`
      : 'Repair again to pick an account that runs them all.'
    : detail;
  return (
    <Callout variant={unverified ? 'info' : 'warning'} role="status" aria-label={headline}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium">{headline}</p>
          {second && <p>{second}</p>}
          {!view.canRepair && <p>{ASK_TO_REPAIR}</p>}
        </div>
        {view.canRepair && (
          <RepairButton
            itemRef={itemRef}
            itemNoun={itemNoun}
            unassigned={view.state === 'unassigned'}
            invalidateKeys={invalidateKeys}
          />
        )}
      </div>
    </Callout>
  );
}
